import React, { useState, useEffect, useCallback } from 'react';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import {
  Box, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Typography, Chip, IconButton, Tooltip,
  Paper, CircularProgress, Alert, Select, FormControl, InputLabel
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import EventIcon from '@mui/icons-material/Event';
import { principalAPI } from '@services/api';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import SchoolCalendar, { TYPE_COLORS } from '@components/SchoolCalendar';

const EVENT_TYPES = [
  { value: 'event', label: 'School Event', color: '#1976d2' },
  { value: 'sports', label: 'Sports', color: '#16a34a' },
  { value: 'cultural', label: 'Cultural', color: '#7c3aed' },
  { value: 'meeting', label: 'Staff Meeting', color: '#0891b2' },
  { value: 'other', label: 'Other', color: '#6b7280' },
];

const EMPTY_FORM = {
  title: '',
  description: '',
  event_date: format(new Date(), 'yyyy-MM-dd'),
  end_date: '',
  event_type: 'event',
  color: '#1976d2',
};

export default function AcademicCalendar() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [calDate, setCalDate] = useState(new Date());
  const [view, setView] = useState('month');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchEvents = useCallback(async (date) => {
    setLoading(true);
    setError('');
    try {
      const start = startOfMonth(date);
      const end = endOfMonth(date);
      const startDate = format(start, 'yyyy-MM-dd');
      const endDate = format(end, 'yyyy-MM-dd');
      const res = await principalAPI.getCalendarEvents({ start_date: startDate, end_date: endDate });
      const rawEvents = res.data.events || [];
      const mapped = rawEvents.map(e => ({
        id: e.id,
        title: e.title,
        start: new Date(e.event_date + 'T00:00:00'),
        end: e.end_date ? new Date(e.end_date + 'T23:59:59') : new Date(e.event_date + 'T23:59:59'),
        resource: e,
        color: e.color || TYPE_COLORS[e.event_type] || '#1976d2',
      }));
      setEvents(mapped);
    } catch (err) {
      setError('Failed to load calendar events');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEvents(calDate);
  }, [fetchEvents, calDate]);

  const handleNavigate = (date) => {
    setCalDate(date);
    fetchEvents(date);
  };

  const openCreate = (slotInfo) => {
    const dateStr = slotInfo?.start ? format(slotInfo.start, 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd');
    setEditingEvent(null);
    setForm({ ...EMPTY_FORM, event_date: dateStr });
    setDialogOpen(true);
  };

  const openEdit = (event) => {
    const r = event.resource;
    if (r.source === 'holiday' || r.source === 'exam') return;
    setEditingEvent(r);
    setForm({
      title: r.title,
      description: r.description || '',
      event_date: r.event_date,
      end_date: r.end_date || '',
      event_type: r.event_type || 'event',
      color: r.color || '#1976d2',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.event_date) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        end_date: form.end_date || null,
        color: TYPE_COLORS[form.event_type] || form.color,
      };
      if (editingEvent) {
        await principalAPI.updateCalendarEvent(editingEvent.id, payload);
      } else {
        await principalAPI.createCalendarEvent(payload);
      }
      setDialogOpen(false);
      fetchEvents(calDate);
    } catch {
      setError('Failed to save event');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await principalAPI.deleteCalendarEvent(deleteTarget.id);
      setDeleteTarget(null);
      fetchEvents(calDate);
    } catch {
      setError('Failed to delete event');
    }
  };

  const EventComponent = ({ event }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {event.title}
      </span>
      {event.resource?.source === 'calendar' && (
        <Box sx={{ display: 'flex', gap: 0.2 }}>
          <EditIcon sx={{ fontSize: 12, cursor: 'pointer', opacity: 0.8 }} onClick={(e) => { e.stopPropagation(); openEdit(event); }} />
          <DeleteIcon sx={{ fontSize: 12, cursor: 'pointer', opacity: 0.8 }} onClick={(e) => { e.stopPropagation(); setDeleteTarget(event.resource); }} />
        </Box>
      )}
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
      <Box sx={{ maxWidth: 1200, mx: 'auto', p: { xs: 2, md: 3 } }}>
        {/* Legend */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          {[
            { label: 'School Event', color: '#1976d2' },
            { label: 'Holiday', color: '#ef4444' },
            { label: 'Exam Period', color: '#f59e0b' },
            { label: 'Sports', color: '#16a34a' },
            { label: 'Cultural', color: '#7c3aed' },
            { label: 'Meeting', color: '#0891b2' },
          ].map(l => (
            <Chip key={l.label} label={l.label} size="small"
              sx={{ bgcolor: l.color, color: '#fff', fontWeight: 500, fontSize: '0.7rem' }} />
          ))}
          <Box sx={{ flex: 1 }} />
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => openCreate(null)} size="small">
            Add Event
          </Button>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        <Paper sx={{ p: 2, borderRadius: 2, position: 'relative' }}>
          {loading && (
            <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(255,255,255,0.7)', zIndex: 10, borderRadius: 2 }}>
              <CircularProgress />
            </Box>
          )}
          <SchoolCalendar
            events={events}
            date={calDate}
            view={view}
            onView={setView}
            onNavigate={handleNavigate}
            onSelectSlot={openCreate}
            onSelectEvent={openEdit}
            selectable
            components={{ event: EventComponent }}
          />
        </Paper>
      </Box>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EventIcon color="primary" />
            {editingEvent ? 'Edit Event' : 'Add Event'}
          </Box>
          <IconButton size="small" onClick={() => setDialogOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField label="Title *" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} fullWidth />
            <TextField label="Description" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} fullWidth multiline rows={2} />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Start Date *" type="date" value={form.event_date} onChange={e => setForm(f => ({ ...f, event_date: e.target.value }))} fullWidth InputLabelProps={{ shrink: true }} />
              <TextField label="End Date" type="date" value={form.end_date} onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))} fullWidth InputLabelProps={{ shrink: true }} />
            </Box>
            <FormControl fullWidth>
              <InputLabel>Event Type</InputLabel>
              <Select value={form.event_type} label="Event Type" onChange={e => setForm(f => ({ ...f, event_type: e.target.value }))}>
                {EVENT_TYPES.map(t => (
                  <MenuItem key={t.value} value={t.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: t.color }} />
                      {t.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving || !form.title.trim()}>
            {saving ? 'Saving…' : editingEvent ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Event</DialogTitle>
        <DialogContent>
          <Typography>Delete <strong>{deleteTarget?.title}</strong>? This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
